// Secret-question ("秘密の質問") service — the enrollment flow (FE-1) and the face-MFA
// fallback (FE-2). Every session-bound call carries the same cognito_session_id that the
// login/face flow established (via getChallengeSession) plus the shared X-Request-ID /
// X-Flow-ID trace headers, keeping one consistent Cognito session end-to-end.
import api from "./api";
import {
  clearChallenge,
  getChallengeSession,
  setChallengeSession,
  tracingHeaders,
} from "./auth";
import { getTxnId } from "../common";
import type { VerifyMfaResult } from "./auth";
import type {
  SecretQuestionAnswer,
  SecretQuestionChallenge,
  SecretQuestionPresetsResponse,
} from "../types/secret-questions";

// FE-1 step 1: the question list (+ required/max counts) for the enrollment form.
export async function getPresets(): Promise<SecretQuestionPresetsResponse> {
  const res = await api.get<SecretQuestionPresetsResponse>(
    "/v1/auth/secret-questions/presets",
    { params: { session_id: getChallengeSession() }, headers: tracingHeaders() }
  );
  return res.data;
}

// FE-1 step 3: persist the chosen questions + answers -> registered = true.
export async function enrollSecretQuestions(answers: SecretQuestionAnswer[]): Promise<void> {
  await api.post(
    "/v1/auth/secret-questions/enroll",
    { answers, session_id: getChallengeSession() },
    { headers: tracingHeaders() }
  );
}

// FE-2 step 1: switch the verify flow to the secret-question fallback (camera-failure path).
// Must return 200 before the challenge can be fetched. The BE responds
// { status: "fallback_enabled" } and does NOT rotate the session; the session_id handling
// below is a defensive no-op kept in case the BE later starts rotating.
export async function enableFallback(reason: string): Promise<void> {
  const res = await api.post<{ session_id?: string; cognito_session_id?: string }>(
    "/v1/auth/secret-questions/enable-fallback",
    { session_id: getChallengeSession(), reason },
    { headers: tracingHeaders() }
  );
  const next = res.data?.session_id ?? res.data?.cognito_session_id;
  if (next) setChallengeSession(next);
}

// One entry of the challenge payload. BE returns `question_text`; `text`/`question` are kept
// as tolerant fallbacks.
interface ChallengeQuestionPayload {
  question_id: string;
  question_text?: string;
  text?: string;
  question?: string;
}

// FE-2 step 2: fetch the enrolled questions to challenge the user with. The BE serves
// `required_count` questions (it used to serve a single one at the top level, which is still
// accepted here).
export async function getSecretQuestionChallenge(): Promise<SecretQuestionChallenge> {
  const res = await api.get<
    { questions?: ChallengeQuestionPayload[]; required_count?: number } & Partial<
      ChallengeQuestionPayload
    >
  >("/v1/auth/secret-questions/challenge", {
    params: { session_id: getChallengeSession() },
    headers: tracingHeaders(),
  });
  const raw = res.data?.questions?.length
    ? res.data.questions
    : res.data?.question_id
      ? [res.data as ChallengeQuestionPayload]
      : [];
  const questions = raw.map((q) => ({
    questionId: q.question_id,
    question: q.question_text ?? q.text ?? q.question ?? "",
  }));
  return { questions, requiredCount: res.data?.required_count ?? questions.length };
}

// FE-2 step 3: verify the answers (one per challenge question, same shape as enroll). On
// success this reuses verify-MFA's outcome (redirect back to Salesforce), so it returns the
// same VerifyMfaResult shape.
export async function verifySecretQuestion(
  answers: SecretQuestionAnswer[]
): Promise<VerifyMfaResult> {
  const res = await api.post<{ redirect_url?: string }>(
    "/v1/auth/verify-secret-question",
    {
      answers,
      session_id: getChallengeSession(),
      txn_id: getTxnId(),
    },
    { headers: tracingHeaders() }
  );
  clearChallenge();
  return { redirectUrl: res.data?.redirect_url };
}
